import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  X,
  CheckCircle,
  XCircle,
  Calendar,
  DollarSign,
  FileText,
  User,
  Clock,
  Loader2,
  Trash2,
  RotateCcw,
  Ban,
  ExternalLink,
  Download,
  AlertTriangle,
  Link2
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  verifyPayment,
  rejectPayment,
  cancelPayment,
  reactivatePayment,
  deletePayment,
  downloadPaymentNotificationReceipt
} from '../../api/wholesale/payments';

const PaymentDetailModal = ({ payment, isOpen, onClose, onSuccess, onAllocate }) => {
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);

  if (!isOpen || !payment) return null;

  const paymentStatus = payment.payment_status || payment.status || 'pending';
  const paymentAmount = parseFloat(payment.payment_amount) || parseFloat(payment.amount) || 0;
  const distributorName =
    payment.distributor_business_name || payment.business_name || payment.distributor_name || '-';

  const statusConfig = {
    pending: {
      label: 'Pending',
      color: 'bg-amber-100 text-amber-700 border-amber-200',
      icon: Clock
    },
    partial: {
      label: 'Partial',
      color: 'bg-blue-100 text-blue-700 border-blue-200',
      icon: DollarSign
    },
    verified: {
      label: 'Verified',
      color: 'bg-green-100 text-green-700 border-green-200',
      icon: CheckCircle
    },
    paid: {
      label: 'Paid',
      color: 'bg-green-100 text-green-700 border-green-200',
      icon: CheckCircle
    },
    rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
    cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: Ban }
  };

  const status = statusConfig[paymentStatus] || statusConfig.pending;
  const StatusIcon = status?.icon || Clock;

  const handleVerify = async () => {
    if (!window.confirm('Are you sure you want to verify this payment?')) return;

    setActionLoading(true);
    try {
      await verifyPayment(payment.id);
      toast.success('Payment verified successfully');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to verify payment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();

    if (!rejectReason.trim()) {
      toast.error('Please enter a rejection reason');
      return;
    }

    setActionLoading(true);
    try {
      await rejectPayment(payment.id, rejectReason);
      toast.success('Payment rejected');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to reject payment');
    } finally {
      setActionLoading(false);
      setRejectReason('');
      setShowRejectForm(false);
    }
  };

  const handleCancel = async (e) => {
    e.preventDefault();

    if (!cancelReason.trim()) {
      toast.error('Please enter a cancellation reason');
      return;
    }

    setActionLoading(true);
    try {
      await cancelPayment(payment.id, cancelReason);
      toast.success('Payment cancelled successfully');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel payment');
    } finally {
      setActionLoading(false);
      setCancelReason('');
      setShowCancelForm(false);
    }
  };

  const handleReactivate = async () => {
    if (
      !window.confirm(
        'Are you sure you want to reactivate this cancelled payment? It will return to pending status.'
      )
    )
      return;

    setActionLoading(true);
    try {
      await reactivatePayment(payment.id);
      toast.success('Payment reactivated successfully');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to reactivate payment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete this payment? This action cannot be undone and will reverse any applied effects.'
      )
    )
      return;

    setActionLoading(true);
    try {
      await deletePayment(payment.id);
      toast.success('Payment deleted successfully');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to delete payment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = () => {
    setShowRejectForm(false);
    setShowCancelForm(false);
    setRejectReason('');
    setCancelReason('');
    onClose();
  };

  const handleOrderClick = () => {
    if (payment.order_id) {
      handleClose();
      navigate(`/wholesale/orders/${payment.order_id}`);
    }
  };

  const handleDownloadReceipt = async () => {
    setDownloadLoading(true);
    try {
      await downloadPaymentNotificationReceipt(payment.id);
      toast.success('Payment notification receipt downloaded');
    } catch (err) {
      toast.error(err.message || 'Failed to download receipt');
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Payment Details</h2>
            <p className="text-sm text-gray-500 mt-1">
              {payment.payment_number || 'Payment Record'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-6">
          {/* Status Badge */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border ${status?.color}`}
              >
                <StatusIcon className="w-4 h-4" />
                {status?.label}
              </span>
              {/* Allocation Status Badge */}
              {(payment.allocation_status || 'unallocated') === 'allocated' ? (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                  <CheckCircle className="w-3 h-3" />
                  Allocated
                </span>
              ) : paymentStatus !== 'cancelled' && paymentStatus !== 'rejected' ? (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                  <AlertTriangle className="w-3 h-3" />
                  Unallocated
                </span>
              ) : null}
            </div>
            <span className="text-2xl font-bold text-gray-900">
              ₦{paymentAmount.toLocaleString()}
            </span>
          </div>

          {/* Unallocated Warning Banner */}
          {(payment.allocation_status || 'unallocated') === 'unallocated' &&
            paymentStatus !== 'cancelled' &&
            paymentStatus !== 'rejected' && (
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="font-semibold text-red-800">Unallocated Payment</p>
                    <p className="text-sm text-red-700">
                      This payment is not linked to any order or customer account balance. It will{' '}
                      <strong>not be included</strong> in financial reports or calculations until it
                      is allocated.
                    </p>
                    {onAllocate && (
                      <button
                        onClick={() => onAllocate(payment)}
                        className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium transition-colors"
                      >
                        <Link2 className="w-4 h-4" />
                        Allocate This Payment
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

          {/* Payment Information */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 mb-3">Payment Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Payment Number</p>
                <p className="font-medium text-gray-900">{payment.payment_number || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Order Number</p>
                {payment.order_id && payment.order_number ? (
                  <button
                    onClick={handleOrderClick}
                    className="font-medium text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1 group"
                  >
                    {payment.order_number}
                    <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ) : (
                  <p className="font-medium text-gray-900">{payment.order_number || '-'}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Payment Method</p>
                <p className="font-medium text-gray-900 capitalize">
                  {payment.payment_method || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Allocation Type</p>
                <p className="font-medium text-gray-900 capitalize">
                  {payment.allocation_type === 'specific_order'
                    ? 'Order Payment'
                    : payment.allocation_type === 'account_balance'
                      ? 'Account Balance'
                      : payment.allocation_type === 'outstanding_payment'
                        ? 'Credit Payment'
                        : payment.allocation_type || '-'}
                </p>
              </div>
            </div>

            {payment.payment_reference && (
              <div>
                <p className="text-sm text-gray-500">Payment Reference</p>
                <p className="font-medium text-gray-900">{payment.payment_reference}</p>
              </div>
            )}

            {payment.transaction_reference && (
              <div>
                <p className="text-sm text-gray-500">Transaction Reference</p>
                <p className="font-medium text-gray-900">{payment.transaction_reference}</p>
              </div>
            )}
          </div>

          {/* Allocations Breakdown */}
          {payment.allocations && payment.allocations.length > 0 && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-blue-900 mb-3">Payment Allocations</h3>
              <div className="space-y-2">
                {payment.allocations.map((allocation, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center text-sm border-b border-blue-100 pb-2 last:border-0 last:pb-0"
                  >
                    <div>
                      <span className="font-medium text-blue-800 capitalize">
                        {allocation.type === 'credit_payment'
                          ? 'Applied to Credit'
                          : allocation.type === 'account_deposit'
                            ? 'Added to Account Balance'
                            : allocation.type.replace('_', ' ')}
                      </span>
                      {allocation.description && (
                        <p className="text-blue-600 text-xs mt-0.5">{allocation.description}</p>
                      )}
                    </div>
                    <span className="font-bold text-blue-900">
                      ₦{parseFloat(allocation.amount).toLocaleString()}
                    </span>
                  </div>
                ))}

                {/* Total Allocation Check */}
                <div className="flex justify-between items-center pt-2 border-t border-blue-200 mt-2 font-bold">
                  <span className="text-blue-900">Total Allocated</span>
                  <span className="text-blue-900">
                    ₦
                    {payment.allocations
                      .reduce((sum, a) => sum + parseFloat(a.amount), 0)
                      .toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Customer Information */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 mb-3">Customer Information</h3>
            <div>
              <p className="text-sm text-gray-500">Business Name</p>
              <p className="font-medium text-gray-900">{distributorName}</p>
            </div>
          </div>

          {/* Date Information */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 mb-3">Date Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Payment Date</p>
                <p className="font-medium text-gray-900">
                  {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : '-'}
                </p>
              </div>
              {payment.due_date && (
                <div>
                  <p className="text-sm text-gray-500">Due Date</p>
                  <p className="font-medium text-gray-900">
                    {new Date(payment.due_date).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Created At</p>
                <p className="font-medium text-gray-900">
                  {payment.created_at ? new Date(payment.created_at).toLocaleString() : '-'}
                </p>
              </div>
              {payment.verified_at && (
                <div>
                  <p className="text-sm text-gray-500">Verified At</p>
                  <p className="font-medium text-gray-900">
                    {new Date(payment.verified_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {payment.payment_notes && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{payment.payment_notes}</p>
            </div>
          )}

          {/* Rejection Reason */}
          {payment.rejection_reason && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-900 mb-2">Rejection Reason</h3>
              <p className="text-red-700">{payment.rejection_reason}</p>
            </div>
          )}

          {/* Cancellation Reason */}
          {payment.cancellation_reason && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Cancellation Reason</h3>
              <p className="text-gray-700">{payment.cancellation_reason}</p>
            </div>
          )}

          {/* Reject Form */}
          {showRejectForm && paymentStatus === 'pending' && (
            <form
              onSubmit={handleReject}
              className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3"
            >
              <h3 className="font-semibold text-red-900">Reject Payment</h3>
              <div>
                <label className="block text-sm font-medium text-red-900 mb-1">
                  Rejection Reason <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter reason for rejecting this payment..."
                  rows={3}
                  required
                  className="w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {actionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Rejecting...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      Confirm Rejection
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRejectForm(false);
                    setRejectReason('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Cancel Form */}
          {showCancelForm && (paymentStatus === 'pending' || paymentStatus === 'verified') && (
            <form
              onSubmit={handleCancel}
              className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3"
            >
              <h3 className="font-semibold text-amber-900">Cancel Payment</h3>
              {paymentStatus === 'verified' && (
                <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 text-amber-800 text-sm">
                  <strong>Warning:</strong> This payment has been verified. Cancelling it will
                  reverse any credit balance updates and order payment allocations.
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-amber-900 mb-1">
                  Cancellation Reason <span className="text-amber-600">*</span>
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Enter reason for cancelling this payment..."
                  rows={3}
                  required
                  className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {actionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <Ban className="w-4 h-4" />
                      Confirm Cancellation
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCancelForm(false);
                    setCancelReason('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Back
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex gap-2">
            {/* Delete button - available for most statuses */}
            {!showRejectForm && !showCancelForm && (
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}

            {/* Pending status actions */}
            {paymentStatus === 'pending' && !showRejectForm && !showCancelForm && (
              <>
                <button
                  onClick={() => setShowCancelForm(true)}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
                >
                  <Ban className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={() => setShowRejectForm(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  Reject
                </button>
                <button
                  onClick={handleVerify}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {actionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Verify
                    </>
                  )}
                </button>
              </>
            )}

            {/* Verified status actions */}
            {paymentStatus === 'verified' && !showCancelForm && (
              <>
                <button
                  onClick={handleDownloadReceipt}
                  disabled={downloadLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {downloadLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Download Receipt
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowCancelForm(true)}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
                >
                  <Ban className="w-4 h-4" />
                  Cancel Payment
                </button>
              </>
            )}

            {/* Cancelled status actions */}
            {paymentStatus === 'cancelled' && (
              <button
                onClick={handleReactivate}
                disabled={actionLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Reactivating...
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-4 h-4" />
                    Reactivate
                  </>
                )}
              </button>
            )}
          </div>

          <button
            onClick={handleClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailModal;
